// Centralized Service Types Configuration
// This file contains all service types used throughout the application

export interface ServiceType {
  value: string;
  label: string;
  unit: string;
  type: 'linear' | 'area' | 'count' | 'roofing' | 'volume';
  category?: string;
  description?: string;
}

// Master list of all service types
export const SERVICE_TYPES: ServiceType[] = [
  // Fencing & Outdoor
  { value: "fence", label: "Fence Installation", unit: "linear ft", type: "linear", category: "Outdoor", description: "Wood, vinyl, chain-link, and custom fencing" },
  { value: "gate", label: "Gate Installation", unit: "each", type: "count", category: "Outdoor", description: "Single, double, and custom gates" },
  { value: "deck", label: "Deck Construction", unit: "sq ft", type: "area", category: "Outdoor", description: "Wood, composite, and custom decking" },
  { value: "patio", label: "Patio Construction", unit: "sq ft", type: "area", category: "Outdoor", description: "Concrete, stone, and brick patios" },
  
  // Roofing & Exterior
  { value: "roof", label: "Roofing", unit: "squares", type: "roofing", category: "Exterior", description: "Shingle, metal, tile, and flat roofing" },
  { value: "gutters", label: "Gutters & Downspouts", unit: "linear ft", type: "linear", category: "Exterior", description: "Aluminum, steel, and seamless gutters" },
  { value: "siding", label: "Siding Installation", unit: "sq ft", type: "area", category: "Exterior", description: "Vinyl, wood, fiber cement siding" },
  { value: "windows", label: "Window Installation", unit: "each", type: "count", category: "Exterior", description: "Replacement and new construction windows" },
  
  // Interior
  { value: "flooring", label: "Flooring Installation", unit: "sq ft", type: "area", category: "Interior", description: "Hardwood, laminate, tile, and carpet" },
  { value: "painting", label: "Painting Services", unit: "sq ft", type: "area", category: "Interior", description: "Interior and exterior painting" },
  { value: "drywall", label: "Drywall Installation", unit: "sq ft", type: "area", category: "Interior", description: "Hanging, taping, and finishing drywall" },
  
  // Systems
  { value: "electrical", label: "Electrical Work", unit: "each", type: "count", category: "Systems", description: "Outlets, switches, fixtures, and panel work" },
  { value: "plumbing", label: "Plumbing Services", unit: "each", type: "count", category: "Systems", description: "Fixtures, pipes, water heaters, and repairs" },
  { value: "hvac", label: "HVAC Services", unit: "each", type: "count", category: "Systems", description: "Installation, repair, and maintenance" },
  
  // Concrete & Masonry
  { value: "concrete", label: "Concrete Work", unit: "sq ft", type: "area", category: "Concrete", description: "Driveways, sidewalks, and foundations" },
  { value: "masonry", label: "Masonry Work", unit: "sq ft", type: "area", category: "Concrete", description: "Brick, stone, and block work" },
  
  // Landscaping
  { value: "landscaping", label: "Landscaping", unit: "sq ft", type: "area", category: "Landscaping", description: "Design, installation, and maintenance" },
  { value: "irrigation", label: "Irrigation Systems", unit: "linear ft", type: "linear", category: "Landscaping", description: "Sprinkler and drip irrigation" },
  
  // Specialty
  { value: "insulation", label: "Insulation", unit: "sq ft", type: "area", category: "Specialty", description: "Attic, wall, and crawl space insulation" },
  { value: "waterproofing", label: "Waterproofing", unit: "sq ft", type: "area", category: "Specialty", description: "Basement and foundation waterproofing" },
  { value: "other", label: "Other Services", unit: "each", type: "count", category: "Specialty", description: "Custom and specialized services" }
];

// Comprehensive list of measurement units for services
export interface MeasurementUnit {
  value: string;
  label: string;
  category: 'length' | 'area' | 'volume' | 'weight' | 'count' | 'time';
}

export const MEASUREMENT_UNITS: MeasurementUnit[] = [
  // Length
  { value: "linear_ft", label: "Linear Feet (ft)", category: "length" },
  { value: "linear_in", label: "Inches (in)", category: "length" },
  { value: "linear_m", label: "Meters (m)", category: "length" },
  { value: "linear_yd", label: "Yards (yd)", category: "length" },
  
  // Area
  { value: "sq_ft", label: "Square Feet (sq ft)", category: "area" },
  { value: "sq_m", label: "Square Meters (sq m)", category: "area" },
  { value: "sq_yd", label: "Square Yards (sq yd)", category: "area" },
  { value: "squares", label: "Squares (100 sq ft)", category: "area" },
  { value: "acres", label: "Acres", category: "area" },
  
  // Volume
  { value: "cubic_yd", label: "Cubic Yards (cu yd)", category: "volume" },
  { value: "cubic_ft", label: "Cubic Feet (cu ft)", category: "volume" },
  { value: "cubic_m", label: "Cubic Meters (cu m)", category: "volume" },
  
  // Weight
  { value: "tons", label: "Tons", category: "weight" },
  { value: "pounds", label: "Pounds (lbs)", category: "weight" },
  { value: "kg", label: "Kilograms (kg)", category: "weight" },
  
  // Count
  { value: "each", label: "Each", category: "count" },
  { value: "pairs", label: "Pairs", category: "count" },
  { value: "sets", label: "Sets", category: "count" },
  { value: "bundles", label: "Bundles", category: "count" },
  { value: "boxes", label: "Boxes", category: "count" },
  { value: "pallets", label: "Pallets", category: "count" },
  
  // Time
  { value: "hours", label: "Hours", category: "time" },
  { value: "days", label: "Days", category: "time" },
  { value: "weeks", label: "Weeks", category: "time" },
];

// Helper functions for measurement units
export function getMeasurementUnitByValue(value: string): MeasurementUnit | undefined {
  return MEASUREMENT_UNITS.find(unit => unit.value === value);
}

export function getMeasurementUnitsByCategory(category: string): MeasurementUnit[] {
  return MEASUREMENT_UNITS.filter(unit => unit.category === category);
}

export function getMeasurementUnitOptions(): { value: string; label: string }[] {
  return MEASUREMENT_UNITS.map(unit => ({
    value: unit.value,
    label: unit.label
  }));
}

// Helper functions
export function getServiceTypeByValue(value: string): ServiceType | undefined {
  return SERVICE_TYPES.find(service => service.value === value);
}

export function getServiceTypesByCategory(category: string): ServiceType[] {
  return SERVICE_TYPES.filter(service => service.category === category);
}

export function getAllCategories(): string[] {
  return [...new Set(SERVICE_TYPES.map(service => service.category).filter(Boolean))];
}

export function getServiceTypeOptions(): { value: string; label: string }[] {
  return SERVICE_TYPES.map(service => ({
    value: service.value,
    label: service.label
  }));
}

// Legacy support - for components that expect simple arrays
export const SERVICE_TYPE_VALUES = SERVICE_TYPES.map(service => service.value);
export const SERVICE_TYPE_LABELS = SERVICE_TYPES.map(service => service.label);

// Default service type for new estimates
export const DEFAULT_SERVICE_TYPE = "fence";
